import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from 'lib/firebase';
import { useStorage } from 'hooks/useStorage';
import { useLanguage } from 'contexts/LanguageContext';
import LoadingScreen from 'components/common/LoadingScreen';

const DocxToPdfTester: React.FC = () => {
  const [docxFile, setDocxFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { uploadFile } = useStorage();
  const { t } = useLanguage();
  const [testResult, setTestResult] = useState<{status: 'success' | 'error', message: string, data?: any} | null>(null);

  // Python Cloud Function endpoint
  const PYTHON_FUNCTION_URL = 'https://us-central1-energiainfocrm.cloudfunctions.net/docx_to_pdf_http';

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          file.name.endsWith('.docx')) {
        setDocxFile(file);
        setError(null);
      } else {
        setError('Csak DOCX fájl fogadható el.');
        setDocxFile(null);
      }
    }
  };

  // Test the Firebase Cloud Function
  const testFirebaseFunction = async () => {
    if (!docxFile) {
      setError('Kérlek válassz ki egy DOCX fájlt a teszteléshez.');
      return;
    }

    setLoading(true);
    setError(null);
    setPdfUrl(null);
    setTestResult(null);

    try {
      // 1. Upload the DOCX file to Firebase Storage
      const docxStoragePath = `test_files/${Date.now()}_${docxFile.name}`;
      const docxUrl = await uploadFile(docxFile, docxStoragePath);

      if (!docxUrl) {
        throw new Error('Nem sikerült feltölteni a DOCX fájlt.');
      }

      // 2. Call the Python Cloud Function
      const response = await fetch(PYTHON_FUNCTION_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          docxUrl,
          fileName: docxFile.name.replace('.docx', '.pdf')
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP hiba: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      // 3. Handle the result
      console.log('Python function result:', result);
      
      // Extract the PDF URL from the result
      let pdfUrl = null;
      if (result && typeof result === 'object' && 'pdfUrl' in result) {
        pdfUrl = result.pdfUrl as string;
      }

      if (pdfUrl) {
        setPdfUrl(pdfUrl);
        setTestResult({
          status: 'success',
          message: 'A konverzió sikeres!',
          data: result
        });
      } else {
        throw new Error('A PDF URL nem található a válaszban.');
      }
    } catch (err) {
      console.error('Error converting file:', err);
      
      // More detailed error message for CORS issues
      let errorMessage = '';
      if (err instanceof Error) {
        if (err.message === 'internal' || err.message.includes('Failed to fetch')) {
          errorMessage = 'CORS hiba: A szerver nem engedélyezi a hozzáférést a helyi fejlesztői környezetből.';
        } else {
          errorMessage = err.message;
        }
      } else {
        errorMessage = String(err);
      }
      
      setError(`Hiba történt a konverzió során: ${errorMessage}`);
      setTestResult({
        status: 'error',
        message: `Hiba történt: ${errorMessage}`,
        data: err
      });
    } finally {
      setLoading(false);
    }
  };

  // Test the HTTP endpoint directly
  const testPythonFunction = async () => {
    if (!docxFile) {
      setError('Kérlek válassz ki egy DOCX fájlt a teszteléshez.');
      return;
    }

    setLoading(true);
    setError(null);
    setPdfUrl(null);
    setTestResult(null);

    try {
      // 1. Upload the DOCX file to Firebase Storage
      const docxStoragePath = `test_files/${Date.now()}_${docxFile.name}`;
      const docxUrl = await uploadFile(docxFile, docxStoragePath);

      if (!docxUrl) {
        throw new Error('Nem sikerült feltölteni a DOCX fájlt.');
      }

      // 2. Call the Python Cloud Function with mode: 'cors'
      const response = await fetch(PYTHON_FUNCTION_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          docxUrl,
          fileName: docxFile.name.replace('.docx', '.pdf')
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP hiba: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      // 3. Handle the result
      console.log('Python function result:', result);
      
      // Extract the PDF URL from the result
      let pdfUrl = null;
      if (result && typeof result === 'object' && 'pdfUrl' in result) {
        pdfUrl = result.pdfUrl as string;
      }

      if (pdfUrl) {
        setPdfUrl(pdfUrl);
        setTestResult({
          status: 'success',
          message: 'A konverzió sikeres!',
          data: result
        });
      } else {
        throw new Error('A PDF URL nem található a válaszban.');
      }
    } catch (err) {
      console.error('Error converting file:', err);
      
      // More detailed error message for CORS issues
      let errorMessage = '';
      if (err instanceof Error) {
        if (err.message.includes('Failed to fetch')) {
          errorMessage = 'CORS hiba: A szerver nem engedélyezi a hozzáférést a helyi fejlesztői környezetből. Ellenőrizd a Cloud Function CORS beállításait.';
        } else {
          errorMessage = err.message;
        }
      } else {
        errorMessage = String(err);
      }
      
      setError(`Hiba történt a konverzió során: ${errorMessage}`);
      setTestResult({
        status: 'error',
        message: `Hiba történt: ${errorMessage}`,
        data: err
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto my-8">
      <h1 className="text-2xl font-semibold mb-6 text-gray-800">DOCX → PDF Konverzió Teszt</h1>
      
      <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <h3 className="text-amber-700 font-medium mb-2">CORS Beállítások</h3>
        <p className="text-sm text-amber-600 mb-2">
          A fejlesztői környezetből való teszteléshez szükséges a CORS beállítása:
        </p>
        <ol className="list-decimal pl-6 text-sm text-amber-600 space-y-1">
          <li>Python Function: A CORS beállítások automatikusan engedélyezve vannak minden forrás számára (*)</li>
        </ol>
      </div>
      
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">DOCX fájl kiválasztása:</label>
        <input
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-500 
            file:mr-4 file:py-2 file:px-4 
            file:rounded-md file:border-0 
            file:text-sm file:font-semibold 
            file:bg-primary-50 file:text-primary-700 
            hover:file:bg-primary-100
            border border-gray-300 rounded-md"
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        
        {docxFile && (
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-sm font-medium text-gray-700">Kiválasztott fájl:</p>
            <p className="text-sm text-gray-600">{docxFile.name} ({Math.round(docxFile.size / 1024)} KB)</p>
          </div>
        )}
      </div>

      <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
        <h3 className="text-green-700 font-medium mb-2">Sikeres Telepítés - Python Cloud Function</h3>
        <p className="text-sm text-green-600 mb-2">
          A Python alapú Cloud Function sikeresen telepítve! A konverzió a docx2pdf könyvtárat használja.
        </p>
        <ul className="list-disc pl-6 text-sm text-green-600 space-y-1">
          <li>Python Function URL: <code>{PYTHON_FUNCTION_URL}</code></li>
        </ul>
      </div>

      <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 mb-8">
        <button
          onClick={testFirebaseFunction}
          disabled={!docxFile || loading}
          className="px-4 py-2 text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Python API Tesztelése
        </button>
        
        <button
          onClick={testPythonFunction}
          disabled={!docxFile || loading}
          className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Python Végpont Tesztelése
        </button>
      </div>

      {loading && (
        <div className="my-8 flex justify-center">
          <LoadingScreen />
        </div>
      )}

      {testResult && (
        <div className={`mt-6 p-4 border rounded-md ${testResult.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <h3 className={`text-lg font-medium ${testResult.status === 'success' ? 'text-green-700' : 'text-red-700'}`}>
            {testResult.message}
          </h3>
          <div className="mt-2 overflow-auto max-h-48 bg-white p-2 rounded border">
            <pre className="text-xs text-gray-700">
              {JSON.stringify(testResult.data, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {pdfUrl && (
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-800 mb-3">Konvertált PDF:</h3>
          <div className="border border-gray-300 rounded-md overflow-hidden">
            <iframe 
              src={pdfUrl} 
              className="w-full h-[600px]" 
              title="Convertált PDF"
            />
          </div>
          <div className="mt-4 flex justify-between items-center">
            <a 
              href={pdfUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Megnyitás új ablakban
            </a>
            <a 
              href={pdfUrl} 
              download
              className="px-4 py-2 text-white bg-green-600 hover:bg-green-700 rounded-md"
            >
              PDF letöltése
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocxToPdfTester; 